/**
 * White-label canonical id lists — single source of truth shared across the
 * frontend (`apps/frontend/src/lib/theme/theme-presets.ts` re-exports these)
 * and the backend (`update-brand-settings.dto.ts` + `users.service.ts` use
 * them for server-side allowlist validation).
 *
 * The frontend index.html boot script intentionally duplicates these lists
 * inline as it runs pre-React and cannot import TS — keep the boot script's
 * `KNOWN_PRESETS` / `KNOWN_FONTS` / `KNOWN_HEADING_FONTS` in sync with the arrays below.
 *
 * See `docs/plans/white-label-platform-plan.md` for the broader design.
 */

export const THEME_PRESET_IDS = [
  "slate",
  "midnight",
  "paper",
  "graphite",
] as const;

export type ThemePresetId = (typeof THEME_PRESET_IDS)[number];

export function isThemePresetId(value: unknown): value is ThemePresetId {
  return (
    typeof value === "string" &&
    (THEME_PRESET_IDS as readonly string[]).includes(value)
  );
}

export const THEME_FONT_IDS = [
  "inter",
  "ibm-plex-sans",
  "source-serif-4",
  "dm-sans",
  "roboto",
] as const;

export type ThemeFontId = (typeof THEME_FONT_IDS)[number];

export function isThemeFontId(value: unknown): value is ThemeFontId {
  return (
    typeof value === "string" &&
    (THEME_FONT_IDS as readonly string[]).includes(value)
  );
}

/**
 * Heading font IDs — applied to `--font-display` via `data-heading-font` on `<html>`.
 * Drives the `font-display` Tailwind class (page titles, h1/h2, card headings, etc.).
 * "sumana" is the Fairgen default heading and maps to no `data-heading-font` attribute.
 */
export const THEME_HEADING_FONT_IDS = [
  "sumana",
  "inter",
  "ibm-plex-sans",
  "playfair-display",
  "source-serif-4",
  "montserrat",
] as const;

export type ThemeHeadingFontId = (typeof THEME_HEADING_FONT_IDS)[number];

export function isThemeHeadingFontId(value: unknown): value is ThemeHeadingFontId {
  return (
    typeof value === "string" &&
    (THEME_HEADING_FONT_IDS as readonly string[]).includes(value)
  );
}

/**
 * Customizable token keys — the CSS variables an org admin may override on
 * top of a preset via the progressive-disclosure "Customize" panel. Persisted
 * on `organizations.theme_overrides` as `Record<string, string>` and applied
 * via inline `style.setProperty` (wins over preset CSS by specificity).
 *
 * Border radius is intentionally excluded — it is uniform across all presets
 * and not user-editable.
 */
export const CUSTOMIZABLE_TOKEN_KEYS = [
  "--background",
  "--card",
  "--sidebar-background",
  "--border",
  "--deep",
] as const;

export type CustomizableTokenKey = (typeof CUSTOMIZABLE_TOKEN_KEYS)[number];

/** A mode-specific override key, e.g. "--background:light" or "--card:dark". */
export type CustomizableTokenModeKey =
  | `${CustomizableTokenKey}:light`
  | `${CustomizableTokenKey}:dark`;

export function isCustomizableTokenKey(
  value: unknown,
): value is CustomizableTokenKey {
  return (
    typeof value === "string" &&
    (CUSTOMIZABLE_TOKEN_KEYS as readonly string[]).includes(value)
  );
}

/**
 * Returns true for both base keys ("--background") and mode-specific keys
 * ("--background:light", "--background:dark"). Used by sanitizeThemeOverrides
 * and ThemeProvider to accept the full set of persisted override keys.
 */
export function isThemeOverrideKey(
  value: unknown,
): value is CustomizableTokenKey | CustomizableTokenModeKey {
  if (typeof value !== "string") return false;
  if (isCustomizableTokenKey(value)) return true;
  const colonIdx = value.lastIndexOf(":");
  if (colonIdx === -1) return false;
  const base = value.slice(0, colonIdx);
  const mode = value.slice(colonIdx + 1);
  return (
    isCustomizableTokenKey(base) && (mode === "light" || mode === "dark")
  );
}

/**
 * Sanitize an incoming `themeOverrides` object to only the allowlisted keys
 * with string values. Returns a NEW object — never mutates the input.
 *
 * Accepts both base keys ("--background") and mode-specific keys
 * ("--background:light", "--background:dark") so that light/dark values can
 * be stored independently and applied by ThemeProvider based on resolved mode.
 *
 * Used by both the backend service (`users.service.ts`) before writing to
 * `organizations.theme_overrides`, and the frontend `ThemeProvider` before
 * calling `style.setProperty`. Identical semantics in both runtimes ensures
 * what is persisted is exactly what the client may apply.
 */
export function sanitizeThemeOverrides(
  input: unknown,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!input || typeof input !== "object") return out;
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    if (isThemeOverrideKey(key) && typeof value === "string" && value.length > 0) {
      out[key] = value;
    }
  }
  return out;
}
